/**
 * Data Cleanup Service
 * Utilities for fixing orphaned records and missing parent links
 */

import { db } from './firebaseConfig';
import { doc, getDoc, updateDoc, arrayRemove, getDocs, collection, query, where } from 'firebase/firestore';
import { FamilyMember } from '../types';

/**
 * Clean orphaned child IDs from a member's childrenIds array
 * Removes any child IDs that don't exist in Firestore
 */
export const cleanOrphanedChildIds = async (memberId: string): Promise<{ removed: string[], kept: string[] }> => {
  try {
    const memberRef = doc(db, 'members', memberId);
    const memberDoc = await getDoc(memberRef);
    
    if (!memberDoc.exists()) {
      throw new Error(`Member ${memberId} not found`);
    }
    
    const member = memberDoc.data() as FamilyMember;
    const childrenIds = member.childrenIds || [];
    const removed: string[] = [];
    const kept: string[] = [];
    
    console.log(`[CLEANUP] Checking ${childrenIds.length} children for ${memberId}`);
    
    for (const childId of childrenIds) {
      const childRef = doc(db, 'members', childId);
      const childDoc = await getDoc(childRef);
      
      if (!childDoc.exists()) {
        console.warn(`[CLEANUP] Orphaned child found: ${childId} doesn't exist, removing from ${memberId}`);
        removed.push(childId);
      } else {
        kept.push(childId);
      }
    }
    
    if (removed.length > 0) {
      // Remove each orphaned ID
      for (const orphanedId of removed) {
        await updateDoc(memberRef, {
          childrenIds: arrayRemove(orphanedId)
        });
      }
      console.log(`[CLEANUP] Removed ${removed.length} orphaned child IDs from ${memberId}`);
    }
    
    return { removed, kept };
  } catch (error) {
    console.error(`[CLEANUP] Error cleaning orphaned children for ${memberId}:`, error);
    throw error;
  }
};

/**
 * Fix missing motherId/fatherId for a child
 * Ensures the child has the correct gender-specific parent reference
 */
export const fixMissingParentLink = async (
  childId: string,
  parentId: string,
  parentGender: 'Male' | 'Female'
): Promise<boolean> => {
  try {
    // Verify parent exists
    const parentRef = doc(db, 'members', parentId);
    const parentDoc = await getDoc(parentRef);
    
    if (!parentDoc.exists()) {
      throw new Error(`Parent ${parentId} not found`);
    }
    
    // Verify child exists
    const childRef = doc(db, 'members', childId);
    const childDoc = await getDoc(childRef);
    
    if (!childDoc.exists()) {
      throw new Error(`Child ${childId} not found`);
    }
    
    const child = childDoc.data() as FamilyMember;
    
    // Determine which field to update
    const genderLower = parentGender.toLowerCase();
    const isMale = genderLower === 'male' || genderLower === 'm';
    const parentField = isMale ? 'fatherId' : 'motherId';
    
    // Check if already set
    const currentValue = isMale ? child.fatherId : child.motherId;
    if (currentValue === parentId) {
      console.log(`[CLEANUP] ${parentField} already correctly set to ${parentId} for child ${childId}`);
      return false;
    }
    
    // Update the child
    await updateDoc(childRef, {
      [parentField]: parentId
    });
    
    console.log(`[CLEANUP] ✓ Set ${parentField}=${parentId} for child ${childId}`);
    
    // Ensure parent has child in childrenIds
    const parent = parentDoc.data() as FamilyMember;
    if (!(parent.childrenIds || []).includes(childId)) {
      await updateDoc(parentRef, {
        childrenIds: [...(parent.childrenIds || []), childId]
      });
      console.log(`[CLEANUP] ✓ Added ${childId} to parent's childrenIds`);
    }
    
    return true;
  } catch (error) {
    console.error(`[CLEANUP] Error fixing parent link for ${childId}:`, error);
    throw error;
  }
};

/**
 * Find all members with missing motherId/fatherId
 * Returns list of members that should have a parent but don't have the link
 */
export const findMissingParentLinks = async (): Promise<{ memberId: string, parentId: string | null, missingField: string }[]> => {
  try {
    const membersRef = collection(db, 'members');
    const snapshot = await getDocs(membersRef);
    
    const missing: { memberId: string, parentId: string | null, missingField: string }[] = [];
    
    snapshot.forEach((doc) => {
      const member = doc.data() as FamilyMember;
      const parentIds = member.parentIds || [];
      
      // If member has parents but no fatherId or motherId, it's missing a link
      if (parentIds.length > 0) {
        if (!member.fatherId && !member.motherId) {
          missing.push({
            memberId: doc.id,
            parentId: parentIds[0] || null,
            missingField: 'both (fatherId and motherId)'
          });
        } else if (!member.fatherId) {
          missing.push({
            memberId: doc.id,
            parentId: null,
            missingField: 'fatherId'
          });
        } else if (!member.motherId) {
          missing.push({
            memberId: doc.id,
            parentId: null,
            missingField: 'motherId'
          });
        }
      }
    });
    
    console.log(`[CLEANUP] Found ${missing.length} members with missing parent links`);
    return missing;
  } catch (error) {
    console.error('[CLEANUP] Error finding missing parent links:', error);
    throw error;
  }
};

/**
 * Generate a cleanup report for a member and all their descendants
 */
export const generateCleanupReport = async (memberId: string): Promise<string> => {
  try {
    const memberRef = doc(db, 'members', memberId);
    const memberDoc = await getDoc(memberRef);
    
    if (!memberDoc.exists()) {
      return `Member ${memberId} not found`;
    }
    
    const member = memberDoc.data() as FamilyMember;
    let report = `\n=== CLEANUP REPORT for ${member.name} (${memberId}) ===\n`;
    
    // Check parent links
    report += `\nParent Links:\n`;
    report += `  - fatherId: ${member.fatherId || 'NOT SET ⚠️'}\n`;
    report += `  - motherId: ${member.motherId || 'NOT SET ⚠️'}\n`;
    report += `  - parentIds: ${member.parentIds?.length || 0} parents\n`;
    
    // Check children
    report += `\nChildren:\n`;
    if (!member.childrenIds || member.childrenIds.length === 0) {
      report += `  - None\n`;
    } else {
      for (const childId of member.childrenIds) {
        const childDoc = await getDoc(doc(db, 'members', childId));
        if (!childDoc.exists()) {
          report += `  - ${childId} (ORPHANED - doesn't exist) ⚠️\n`;
        } else {
          const childName = (childDoc.data() as FamilyMember).name;
          report += `  - ${childName} (${childId})\n`;
        }
      }
    }
    
    report += `\n`;
    return report;
  } catch (error) {
    console.error('[CLEANUP] Error generating report:', error);
    throw error;
  }
};
